import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { subscribe } from '../../api';

interface SubscribeFormProps {
  variant?: 'dark' | 'light';
}

export default function SubscribeForm({ variant = 'dark' }: SubscribeFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [emailError, setEmailError] = useState('');
  const submittingRef = useRef(false);

  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!email) return;
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    toast.dismiss();
    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await subscribe(email, name || undefined);
      const msg = res?.message || '';
      if (msg.toLowerCase().includes('already')) {
        setSuccessMsg('You are already subscribed!');
      } else {
        setSuccessMsg('Thank you! Check your email to confirm your subscription.');
      }
    } catch {
      setEmailError('Failed to subscribe. Please try again.');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const isDark = variant === 'dark';

  const inputStyle: React.CSSProperties = isDark ? {
    padding: '0.75rem 1rem',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(184,134,11,0.25)',
    color: '#f0f2f5',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    outline: 'none',
    flex: 1,
    minWidth: 0,
  } : {
    padding: '0.75rem 1rem',
    background: '#FFFFFF',
    border: '1px solid rgba(10,22,40,0.15)',
    color: '#1A2A3A',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.95rem',
    outline: 'none',
    flex: 1,
    minWidth: 0,
  };

  const btnStyle: React.CSSProperties = {
    padding: '0.75rem 1.5rem',
    background: '#B8860B',
    color: '#FFFFFF',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.2s',
    opacity: loading ? 0.6 : 1,
  };

  if (successMsg) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', maxWidth: '540px', padding: '1rem 1.5rem', background: isDark ? 'rgba(255,255,255,0.06)' : '#F0EBE0', border: `1px solid ${isDark ? 'rgba(184,134,11,0.25)' : 'rgba(10,22,40,0.09)'}` }}>
        <span style={{ color: '#B8860B', fontSize: '1.25rem' }}>✓</span>
        <p style={{ color: isDark ? '#f0f2f5' : '#1A2A3A', fontSize: '0.95rem' }}>{successMsg}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '540px', width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
        <input
          type="text"
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
        <input
          type="email"
          required
          placeholder="Email address"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
          style={{ ...inputStyle, borderColor: emailError ? '#e05555' : (isDark ? 'rgba(184,134,11,0.25)' : 'rgba(10,22,40,0.15)') }}
        />
        <button type="submit" disabled={loading} style={btnStyle}>
          {loading ? 'Subscribing...' : 'Subscribe'}
        </button>
      </div>
      {emailError && <p style={{ color: '#e05555', fontSize: '0.78rem' }}>{emailError}</p>}
    </form>
  );
}
